import { ApolloClient, useQuery } from '@apollo/client';
import { ToastDurationOptions } from '@ui-components/neo.tax';
import { ReactComponent as SuccessIcon } from '@ui-components/neo.tax/dist/icons/success-circle.svg';
import { useEffect } from 'react';

import { evictCache } from '../apollo';
import { BACKGROUND_OPERATION_COMPLETION_CONFIGS, POLLING_INTERVAL_MS } from '../constants';
import {
  GetBackgroundOperationsQuery,
  GetBackgroundOperationsQueryVariables,
  GET_BACKGROUND_OPERATIONS,
  Query,
} from '../graphql';
import { ToastConfigState } from '../state';
import { useCurrentCompanyId } from './useCurrentCompanyId';

export type BackgroundOperation = GetBackgroundOperationsQuery['getBackgroundOperations'][number];

const evictCacheOnBackgroundOperationCompletion = (
  client: ApolloClient<any>,
  data: GetBackgroundOperationsQuery,
  previousData: GetBackgroundOperationsQuery,
): void => {
  const queriesToEvict: Set<keyof Query> = new Set();

  const previouslyIncompleteBackgroundOperations = previousData.getBackgroundOperations.filter(
    (backgroundOperation) => backgroundOperation.progressPercent < 100,
  );

  for (const config of BACKGROUND_OPERATION_COMPLETION_CONFIGS) {
    const previouslyIncompleteForOperation = previouslyIncompleteBackgroundOperations.filter(
      (op) => op.name === config.operationName,
    );

    if (previouslyIncompleteForOperation.length === 0) {
      continue;
    }

    const newlyCompleted = data.getBackgroundOperations.find(
      (op) => previouslyIncompleteForOperation.some((prev) => prev.id === op.id) && op.progressPercent === 100,
    );

    if (newlyCompleted) {
      for (const cacheKey of config.cacheKeysToEvict) {
        queriesToEvict.add(cacheKey);
      }
      ToastConfigState.pushToast({
        id: newlyCompleted.id,
        title: config.toastTitle,
        icon: <SuccessIcon />,
        description: config.toastDescription,
        duration: ToastDurationOptions.LONG,
        severity: 'success',
      });
    }
  }

  if (queriesToEvict.size > 0) {
    evictCache(client.cache, ['getBackgroundOperations', ...Array.from(queriesToEvict)]);
  }
};

export const useBackgroundOperations = ():
  | {
      backgroundOperations: null;
      loading: true;
    }
  | {
      backgroundOperations: BackgroundOperation[];
      loading: false;
    } => {
  const companyId = useCurrentCompanyId();

  const { client, data, loading, previousData } = useQuery<
    GetBackgroundOperationsQuery,
    GetBackgroundOperationsQueryVariables
  >(GET_BACKGROUND_OPERATIONS, {
    variables: { companyId },
    fetchPolicy: 'cache-and-network',
    pollInterval: POLLING_INTERVAL_MS,
  });

  useEffect(() => {
    if (!data || !previousData) return;
    evictCacheOnBackgroundOperationCompletion(client, data, previousData);
  }, [data, previousData, client]);

  if (loading) {
    return { backgroundOperations: null, loading: true };
  }

  const backgroundOperations = data?.getBackgroundOperations || [];

  return { backgroundOperations, loading: false };
};
